import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Trash2, Wrench, AlertCircle, Search, Bike, FileText, X, UserPlus, Package, ShoppingCart, Shield } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { useFilial } from '@/lib/filial'
import { navegarLista } from '@/lib/keyboard'
import ModalNovoCliente from '@/components/ModalNovoCliente'

interface ItemForm {
  produto_id: number | null
  descricao: string
  quantidade: number
  preco_unit: number
  desconto: number
  origem_mp: 'estoque_proprio' | 'cliente_traz'
  garantia_dias: number
  eh_servico: boolean   // 01/07: snapshot de produto.tipo=='servico' — só p/ UI (badge/origem/split); backend recalcula do produto
}

const EMPTY_ITEM: ItemForm = {
  produto_id: null,
  descricao: '',
  quantidade: 1,
  preco_unit: 0,
  desconto: 0,
  origem_mp: 'estoque_proprio',
  garantia_dias: 0,
  eh_servico: false,
}

export default function NovaOSPage() {
  const nav = useNavigate()
  const [clienteId, setClienteId] = useState<number | null>(null)
  const [camposCustom, setCamposCustom] = useState<Record<string, string>>({})
  const [customKey, setCustomKey] = useState('')
  const [customVal, setCustomVal] = useState('')
  const [prioridade, setPrioridade] = useState('normal')
  const [tecnicoId, setTecnicoId] = useState<number | null>(null)
  const [dataPrevista, setDataPrevista] = useState('')  // só DATA (YYYY-MM-DD), sem hora
  const [descricaoProblema, setDescricaoProblema] = useState('')
  const [observacoesCliente, setObservacoesCliente] = useState('')
  const [observacoesInternas, setObservacoesInternas] = useState('')
  const [itens, setItens] = useState<ItemForm[]>([])
  const [novoItem, setNovoItem] = useState<ItemForm>(EMPTY_ITEM)
  // FEATURE 06/07: OS de garantia/retorno (marca permanente; itens sempre R$0; link
  // opcional pelo nº da OS original). Substitui o produto avulso "RETORNO OU GARANTIA".
  const [ehGarantia, setEhGarantia] = useState(false)
  const [osOriginalNumero, setOsOriginalNumero] = useState('')
  const [osBusca, setOsBusca] = useState('')   // barra de busca da OS original (garantia)
  // Ao LIGAR a garantia, zera os itens já lançados (garantia é R$0) pra o total bater.
  function toggleGarantia(on: boolean) {
    setEhGarantia(on)
    if (on) {
      setItens(prev => prev.map(it => ({ ...it, preco_unit: 0, desconto: 0 })))
      setNovoItem(ni => ({ ...ni, preco_unit: 0, desconto: 0 }))
    }
  }

  // Busca digitável de cliente — server-side (lista de clientes grande, o GET
  // pagina; filtrar no front perderia quem está fora da 1ª página).
  const [buscaCliente, setBuscaCliente] = useState('')
  const [abrirCliente, setAbrirCliente] = useState(false)
  const [destaqueCli, setDestaqueCli] = useState(0)        // item destacado p/ navegação ↓↑Enter
  const [showNovoCliente, setShowNovoCliente] = useState(false)  // modal cadastro rápido
  const clienteRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (clienteRef.current && !clienteRef.current.contains(e.target as Node)) setAbrirCliente(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])
  const { data: clientes = [] } = useQuery<any[]>({
    queryKey: ['os-clientes', buscaCliente],
    queryFn: async () => (await api.get('/cadastros/pessoas', {
      params: { tipo: 'cliente', q: buscaCliente.trim() || undefined, limit: 20 },
    })).data,
    enabled: abrirCliente,
  })
  const selecionarCliente = (c: any) => {
    setClienteId(c.id)
    setBuscaCliente(c.nome)
    setAbrirCliente(false)
    setEquipamentoId(null)  // troca de cliente → zera bike selecionada
  }
  const filialAtualId = useFilial(s => s.filialAtual?.id ?? null)
  const { data: tecnicos = [] } = useQuery<any[]>({
    queryKey: ['tecnicos-os', filialAtualId],
    // filial_id (explícito) filtra pela filial da OS mesmo em modo consolidado,
    // onde o header X-Filial-Id não é enviado. filial:true = fallback (sem filial selecionada).
    queryFn: async () => (await api.get('/cadastros/colaboradores', { params: { tipo: 'tecnico', ativos: true, filial: true, filial_id: filialAtualId ?? undefined } }).catch(() => ({ data: [] }))).data || [],
  })
  // Vendedor da OS (colaborador tipo=vendedor — ex.: a própria oficina). Vem
  // pré-selecionado no 1º vendedor ativo, mas pode ser trocado.
  const [vendedorCadId, setVendedorCadId] = useState<number | null>(null)
  const { data: vendedores = [] } = useQuery<any[]>({
    queryKey: ['vendedores-os', filialAtualId],
    queryFn: async () => (await api.get('/cadastros/colaboradores', { params: { tipo: 'vendedor', ativos: true, filial: true, filial_id: filialAtualId ?? undefined } }).catch(() => ({ data: [] }))).data || [],
  })
  useEffect(() => {
    // Pré-seleciona "OFICINA" (vendedor genérico da oficina) por padrão; se não
    // existir, cai no 1º da lista. O operador pode trocar no dropdown.
    if (vendedorCadId == null && vendedores.length > 0) {
      const oficina = vendedores.find((v: any) => (v.nome || '').trim().toUpperCase() === 'OFICINA')
      setVendedorCadId((oficina || vendedores[0]).id)
    }
  }, [vendedores, vendedorCadId])

  // Equipamento (bike) do cliente — prontuário. Lista as bikes do cliente selecionado.
  const qc = useQueryClient()
  const [equipamentoId, setEquipamentoId] = useState<number | null>(null)
  const [showNovaBike, setShowNovaBike] = useState(false)
  const [showProntuario, setShowProntuario] = useState<number | null>(null)
  const { data: bikesCliente = [] } = useQuery<any[]>({
    queryKey: ['os-equipamentos', clienteId],
    queryFn: async () => (await api.get('/servicos/equipamentos', { params: { cliente_id: clienteId } })).data,
    enabled: !!clienteId,
  })
  // FEATURE 06/07: produtos/serviços do cliente que TÊM garantia (vigente ou vencida) —
  // painel na Nova OS pra o atendente ver e abrir garantia com 1 clique.
  const { data: garantiasCliente = [] } = useQuery<any[]>({
    queryKey: ['garantias-cliente', clienteId],
    queryFn: async () => (await api.get('/servicos/garantias', { params: { cliente_id: clienteId } })).data,
    enabled: !!clienteId,
  })
  // Abre a OS de garantia (filha, R$0) a partir da OS de origem, cobrindo só aquele item.
  const abrirGarantia = useMutation({
    mutationFn: async (g: any) => (await api.post(`/servicos/${g.os_id}/garantia`, {
      descricao_problema: `Garantia — ${g.descricao}`,
      itens_cobertos: [g.item_id],
    })).data,
    onSuccess: (os: any) => { toast.success(`OS de garantia #${os.numero} aberta`); nav(`/servicos/${os.id}`) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao abrir garantia'),
  })
  const fmtD = (s?: string | null) => (s ? s.split('-').reverse().join('/') : '—')
  // FEATURE 06/07: busca de OS pra vincular a ORIGINAL numa OS de garantia (nº ou cliente).
  const { data: osBuscaResultados = [] } = useQuery<any[]>({
    queryKey: ['os-buscar-original', clienteId, osBusca],
    queryFn: async () => (await api.get('/servicos/buscar', {
      params: { q: osBusca || undefined, cliente_id: clienteId || undefined, limit: 15 },
    })).data,
    enabled: ehGarantia && (!!clienteId || osBusca.trim().length > 0),
  })
  // Seleciona a bike e auto-preenche os campos da OS com os dados dela.
  const selecionarBike = (b: any) => {
    setEquipamentoId(b.id)
    const cc: Record<string, string> = {}
    if (b.marca) cc['marca'] = b.marca
    if (b.modelo) cc['modelo'] = b.modelo
    if (b.ano) cc['ano'] = String(b.ano)
    if (b.cor) cc['cor'] = b.cor
    if (b.numero_serie) cc['nº série'] = b.numero_serie
    Object.entries(b.campos_custom || {}).forEach(([k, v]) => { if (v) cc[k] = String(v) })
    setCamposCustom(prev => ({ ...prev, ...cc }))
  }

  // Busca digitável de produto/serviço no item — server-side (catálogo grande,
  // o endpoint pagina em 300 por descrição, então filtrar no front perderia itens).
  const [buscaProd, setBuscaProd] = useState('')
  const [abrirBusca, setAbrirBusca] = useState(false)
  const [destaqueProd, setDestaqueProd] = useState(0)      // item destacado p/ navegação ↓↑Enter
  // 01/07 (pedido do usuário): busca de PEÇAS e SERVIÇOS separada por aba.
  // Peça → tipo=produto ; Serviço → tipo=servico (filtro no próprio endpoint /cadastros/produtos).
  const [abaBusca, setAbaBusca] = useState<'peca' | 'servico'>('peca')
  const buscaRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (buscaRef.current && !buscaRef.current.contains(e.target as Node)) setAbrirBusca(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const { data: produtos = [] } = useQuery<any[]>({
    queryKey: ['os-produtos', buscaProd, abaBusca],
    queryFn: async () => (await api.get('/cadastros/produtos', {
      params: {
        q: buscaProd.trim() || undefined,
        limit: 20,
        tipo: abaBusca === 'servico' ? 'servico' : 'produto',   // separa peça × serviço no servidor
      },
    })).data,
    enabled: abrirBusca,
  })

  // OS de garantia é sempre R$0 (retrabalho coberto) — total exibido zera pra bater com o backend.
  const total = useMemo(() => ehGarantia ? 0 : itens.reduce((acc, it) =>
    acc + (it.preco_unit * it.quantidade - it.desconto), 0,
  ), [itens, ehGarantia])
  // Split de valores: peças (mercadoria) × serviços (mão de obra) — espelha valor_pecas/valor_servicos do backend
  const totalServicos = useMemo(() => ehGarantia ? 0 : itens.filter(it => it.eh_servico)
    .reduce((a, it) => a + (it.preco_unit * it.quantidade - it.desconto), 0), [itens, ehGarantia])
  const totalPecas = total - totalServicos

  const selecionarProduto = (prod: any) => {
    setNovoItem({
      ...novoItem,
      produto_id: prod.id,
      descricao: prod.descricao || '',
      preco_unit: Number(prod.preco_venda || 0),
      eh_servico: prod.tipo === 'servico',
    })
    setBuscaProd(`${prod.codigo} — ${prod.descricao}`)
    setAbrirBusca(false)
  }

  // Troca a aba de busca (Peças ↔ Serviços): limpa o texto e o item pendente pra não misturar tipos,
  // e já abre a lista do novo tipo (mostra os primeiros 20 sem digitar nada).
  const trocarAba = (aba: 'peca' | 'servico') => {
    if (aba === abaBusca) return
    setAbaBusca(aba)
    setBuscaProd('')
    setDestaqueProd(0)
    setNovoItem(EMPTY_ITEM)
    setAbrirBusca(true)
  }

  const addItem = () => {
    if (!novoItem.produto_id) {
      toast.error(abaBusca === 'servico' ? 'Selecione um serviço' : 'Selecione uma peça')
      return
    }
    // Garantia: item entra sempre a R$0 (retrabalho coberto) pra bater com o total.
    const itemAdd = ehGarantia ? { ...novoItem, preco_unit: 0, desconto: 0 } : novoItem
    setItens([...itens, itemAdd])
    setNovoItem(EMPTY_ITEM)
    setBuscaProd('')
  }

  // Monta o corpo da OS (compartilhado entre "Criar OS" e "Abrir e cobrar no balcão").
  const montarBody = () => {
    // Se o usuário preencheu um produto mas esqueceu de clicar em "Adicionar item",
    // inclui esse item pendente automaticamente pra não criar OS sem valor.
    const itensFinal = novoItem.produto_id ? [...itens, novoItem] : itens
    return {
      cliente_id: clienteId,
      equipamento_id: equipamentoId || undefined,
      prioridade,
      tecnico_cad_id: tecnicoId,
      vendedor_cad_id: vendedorCadId || undefined,
      data_prevista: dataPrevista || undefined,
      descricao_problema: descricaoProblema || undefined,
      observacoes_cliente: observacoesCliente || undefined,
      observacoes_internas: observacoesInternas || undefined,
      campos_custom: Object.keys(camposCustom).length > 0 ? camposCustom : undefined,
      // FEATURE 06/07: marca de garantia + nº opcional da OS original (backend resolve).
      eh_garantia: ehGarantia,
      os_original_numero: ehGarantia && osOriginalNumero ? Number(osOriginalNumero) : undefined,
      itens: itensFinal.map(it => ({
        produto_id: it.produto_id,
        descricao: it.descricao || undefined,
        quantidade: it.quantidade,
        preco_unit: ehGarantia ? 0 : it.preco_unit,   // garantia é sempre R$0
        desconto: ehGarantia ? 0 : it.desconto,
        origem_mp: it.origem_mp,
        garantia_dias: it.garantia_dias,
      })),
    }
  }

  const criar = useMutation({
    mutationFn: async () => (await api.post('/servicos', montarBody())).data,
    onSuccess: (os) => {
      toast.success(`OS #${os.numero} criada`)
      nav(`/servicos/${os.id}`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao criar OS'),
  })

  // "Abrir e cobrar no balcão": cria a OS, faz o fast-forward pra 'concluida'
  // (concluir-balcao já baixa estoque) e joga o operador no PDV com a OS carregada.
  const criarBalcao = useMutation({
    mutationFn: async () => {
      const os = (await api.post('/servicos', montarBody())).data
      await api.post(`/servicos/${os.id}/concluir-balcao`)
      return os
    },
    onSuccess: (os) => {
      toast.success(`OS #${os.numero} pronta — cobrando no PDV`)
      nav('/pdv', { state: { balcaoOS: os.numero } })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao abrir no balcão'),
  })

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <button className="btn-ghost" onClick={() => nav('/servicos')}>
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Wrench className="w-6 h-6" /> Nova OS
        </h1>
      </div>

      {/* Bloco cliente */}
      <div className="card p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="relative" ref={clienteRef}>
          <label className="text-xs text-gray-500 uppercase">Cliente *</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="input pl-9"
              placeholder="Digite o nome do cliente..."
              value={buscaCliente}
              autoComplete="off"
              onChange={e => {
                setBuscaCliente(e.target.value)
                setAbrirCliente(true)
                setDestaqueCli(0)        // texto mudou → volta destaque p/ o 1º
                if (clienteId) setClienteId(null)
              }}
              onFocus={() => setAbrirCliente(true)}
              // ↓↑ navega, Enter seleciona o destacado, Esc fecha
              onKeyDown={navegarLista(clientes, destaqueCli, setDestaqueCli,
                selecionarCliente, () => setAbrirCliente(false))}
            />
          </div>
          {abrirCliente && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-30 max-h-80 overflow-y-auto">
              {clientes.length === 0 ? (
                <div className="p-3 text-center text-sm text-gray-500">Nenhum cliente encontrado</div>
              ) : (
                clientes.map((c: any, idx: number) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => selecionarCliente(c)}
                    onMouseEnter={() => setDestaqueCli(idx)}
                    ref={el => { if (idx === destaqueCli) el?.scrollIntoView({ block: 'nearest' }) }}
                    className={`w-full px-3 py-2 text-left border-b last:border-0 ${idx === destaqueCli ? 'bg-brand-50' : 'hover:bg-gray-50'}`}
                  >
                    <p className="text-sm font-medium truncate">{c.nome}</p>
                    {(c.cpf_cnpj || c.telefone) && (
                      <span className="text-xs text-gray-500">{c.cpf_cnpj || c.telefone}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
          {/* Cadastro rápido FORA do dropdown (botão fixo abaixo do campo).
              Cores dark-aware: text-brand-700 + hover:bg-brand-50 têm override em .app-dark
              (antes era bg-brand-50/60 + hover:bg-brand-100, que NÃO têm override e ficavam
              claras/erradas no tema escuro). */}
          <button
            type="button"
            onClick={() => setShowNovoCliente(true)}
            className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:bg-brand-50 px-2 py-1 rounded"
          >
            <UserPlus size={13} /> Cadastrar novo cliente
          </button>
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Prioridade</label>
          <select className="input" value={prioridade} onChange={e => setPrioridade(e.target.value)}>
            <option value="baixa">Baixa</option>
            <option value="normal">Normal</option>
            <option value="alta">Alta</option>
            <option value="urgente">URGENTE</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">
            Mecânico / Técnico <span className="text-rose-500">*</span>
          </label>
          <select className={`input ${!tecnicoId ? 'ring-1 ring-amber-400' : ''}`}
            value={tecnicoId || ''} onChange={e => setTecnicoId(Number(e.target.value) || null)}>
            <option value="">—</option>
            {tecnicos.map((t: any) => (
              <option key={t.id} value={t.id}>{t.nome}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Vendedor (oficina)</label>
          <select className="input" value={vendedorCadId || ''} onChange={e => setVendedorCadId(Number(e.target.value) || null)}>
            <option value="">—</option>
            {vendedores.map((v: any) => (
              <option key={v.id} value={v.id}>{v.nome}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 uppercase">Data prevista</label>
          {/* 30/06 (pedido do usuário): só DATA — removido o toggle de mês e a hora/minuto. */}
          <input
            type="date"
            className="input"
            value={dataPrevista}
            onChange={e => setDataPrevista(e.target.value)}
          />
        </div>
      </div>

      {/* FEATURE 06/07: OS de Garantia/Retorno — marca permanente (destaque visual + R$0).
          Substitui o antigo produto avulso "RETORNO OU GARANTIA". */}
      <div className={`card p-4 mb-4 border-l-4 transition-colors ${ehGarantia ? 'os-garantia !border-l-rose-500' : 'border-l-transparent'}`}>
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input type="checkbox" className="w-4 h-4 accent-rose-600" checked={ehGarantia}
            onChange={e => toggleGarantia(e.target.checked)} />
          <span className="flex items-center gap-1.5 text-sm font-semibold text-ink-800">
            <Shield size={15} className={ehGarantia ? 'text-rose-600' : 'text-gray-400'} />
            OS de Garantia / Retorno
          </span>
          <span className="text-xs text-gray-500">— sem cobrança (itens entram a R$ 0)</span>
        </label>
        {ehGarantia && (
          <div className="mt-3">
            <label className="text-xs text-gray-500 uppercase">Vincular à OS original (opcional)</label>
            {osOriginalNumero ? (
              // Já escolheu: mostra a OS vinculada + botão pra trocar.
              <div className="mt-1 flex items-center gap-2 rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-sm">
                <Shield size={13} className="text-rose-500" />
                <span className="font-medium">OS #{osOriginalNumero}</span>
                <button type="button" className="ml-auto text-xs text-rose-600 hover:underline"
                  onClick={() => { setOsOriginalNumero(''); setOsBusca('') }}>trocar</button>
              </div>
            ) : (
              <div className="relative mt-1">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input className="input pl-8" placeholder="Buscar OS por nº ou cliente..."
                  value={osBusca} onChange={e => setOsBusca(e.target.value)} />
                {(osBusca.trim() || clienteId) && osBuscaResultados.length > 0 && (
                  <div className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                    {osBuscaResultados.map((o: any) => (
                      <button key={o.id} type="button"
                        onClick={() => { setOsOriginalNumero(String(o.numero)); setOsBusca('') }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-rose-50 border-b border-gray-100 last:border-0">
                        <span className="font-mono font-semibold">#{o.numero}</span>
                        <span className="text-gray-500"> · {o.cliente_nome || '—'} · {fmtD((o.data_abertura || '').slice(0, 10))}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* FEATURE 06/07: painel de GARANTIAS do cliente — produtos/serviços que ele comprou
          e ainda têm garantia (vigente/vencida). "Abrir garantia" cria a OS filha R$0. */}
      {clienteId && garantiasCliente.length > 0 && (
        <div className="card p-4 mb-4 border-l-4 border-l-rose-400">
          <div className="text-xs text-gray-500 uppercase flex items-center gap-1.5 mb-2">
            <Shield size={14} className="text-rose-500" /> Garantias deste cliente
          </div>
          <div className="space-y-1.5">
            {garantiasCliente.map((g: any) => (
              <div key={g.item_id} className="flex items-center gap-2 flex-wrap rounded-lg border border-gray-200 p-2.5 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{g.descricao}</p>
                  <p className="text-[11px] text-gray-500">
                    OS #{g.os_numero} · comprado {fmtD(g.comprado_em)} · garantia até {fmtD(g.garantia_ate)} ({g.garantia_dias}d)
                  </p>
                </div>
                {g.vigente ? (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 whitespace-nowrap">
                    VIGENTE · faltam {g.dias_restantes}d
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-600 whitespace-nowrap">
                    VENCIDA há {Math.abs(g.dias_restantes)}d
                  </span>
                )}
                {g.vigente && (
                  <button type="button" onClick={() => abrirGarantia.mutate(g)} disabled={abrirGarantia.isPending}
                    className="px-2.5 py-1 rounded bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-semibold whitespace-nowrap flex items-center gap-1 disabled:opacity-60">
                    <Shield size={12} /> Abrir garantia
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bike / Equipamento do cliente (prontuário) */}
      {clienteId && (
        <div className="card p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-gray-500 uppercase flex items-center gap-1.5"><Bike size={14} /> Bike do cliente</div>
            <button type="button" onClick={() => setShowNovaBike(true)}
              className="text-xs text-brand-600 font-semibold flex items-center gap-1 hover:text-brand-700">
              <Plus size={13} /> Nova bike
            </button>
          </div>
          {bikesCliente.length === 0 ? (
            <p className="text-xs text-gray-400">Cliente sem bike cadastrada. Clique em "Nova bike" pra cadastrar.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {bikesCliente.map((b: any) => (
                <div key={b.id}
                  className={`rounded-lg border p-2.5 cursor-pointer transition-colors ${
                    equipamentoId === b.id ? 'border-brand-500 bg-brand-50' : 'border-gray-200 hover:border-brand-300'}`}
                  onClick={() => selecionarBike(b)}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{b.descricao}{b.cor ? ` · ${b.cor}` : ''}</p>
                      {b.numero_serie && <span className="text-[11px] text-gray-500 font-mono">série {b.numero_serie}</span>}
                    </div>
                    <button type="button" onClick={e => { e.stopPropagation(); setShowProntuario(b.id) }}
                      title="Ver prontuário"
                      className="shrink-0 text-gray-400 hover:text-brand-600"><FileText size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {equipamentoId && <p className="text-[11px] text-brand-600 mt-2">✓ Bike selecionada — dados puxados pros campos abaixo.</p>}
        </div>
      )}

      {/* Campos adicionais (livres) */}
      {(
        <div className="card p-4 mb-4">
          <div className="text-xs text-gray-500 uppercase mb-2">Campos adicionais</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
            {Object.entries(camposCustom).map(([k, v]) => (
              <div key={k}>
                <label className="text-xs text-gray-500">{k}</label>
                <div className="flex gap-1">
                  <input
                    className="input"
                    value={v}
                    onChange={e => setCamposCustom({ ...camposCustom, [k]: e.target.value })}
                  />
                  <button
                    className="btn-ghost"
                    onClick={() => {
                      const c = { ...camposCustom }
                      delete c[k]
                      setCamposCustom(c)
                    }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-xs text-gray-500">Adicionar campo (chave)</label>
              <input
                className="input"
                placeholder="ex: placa, marca, tecido..."
                value={customKey}
                onChange={e => setCustomKey(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-500">Valor</label>
              <input
                className="input"
                value={customVal}
                onChange={e => setCustomVal(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                if (!customKey.trim()) return
                setCamposCustom({ ...camposCustom, [customKey.trim()]: customVal })
                setCustomKey(''); setCustomVal('')
              }}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Problema */}
      <div className="card p-4 mb-4">
        <label className="text-xs text-gray-500 uppercase">Problema relatado pelo cliente</label>
        <textarea
          className="input min-h-[80px]"
          placeholder="O que o cliente reportou..."
          value={descricaoProblema}
          onChange={e => setDescricaoProblema(e.target.value)}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-xs text-gray-500 uppercase">Obs. cliente (visível no portal)</label>
            <textarea
              className="input min-h-[60px]"
              value={observacoesCliente}
              onChange={e => setObservacoesCliente(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase">Obs. internas</label>
            <textarea
              className="input min-h-[60px]"
              value={observacoesInternas}
              onChange={e => setObservacoesInternas(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Itens */}
      <div className="card p-4 mb-4">
        <div className="text-xs text-gray-500 uppercase mb-2">Itens — peças e serviços</div>

        {itens.length > 0 && (
          <table className="w-full text-sm mb-3">
            <thead className="text-xs text-gray-500 border-b">
              <tr>
                <th className="text-left py-1">Descrição</th>
                <th className="text-right py-1">Qtd</th>
                <th className="text-right py-1">Preço</th>
                <th className="text-right py-1">Desc.</th>
                <th className="text-right py-1">Total</th>
                <th className="text-left py-1">Origem</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {itens.map((it, i) => {
                const prod = produtos.find((p: any) => p.id === it.produto_id)
                return (
                  <tr key={i} className="border-b">
                    <td className="py-1">
                      <span className={`mr-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded align-middle
                        ${it.eh_servico ? 'bg-violet-100 text-violet-700' : 'bg-brand-100 text-brand-700'}`}>
                        {it.eh_servico ? 'SERV' : 'PEÇA'}
                      </span>
                      {it.descricao || prod?.descricao}
                    </td>
                    <td className="py-1 text-right font-mono">{it.quantidade}</td>
                    <td className="py-1 text-right font-mono">{formatMoney(it.preco_unit)}</td>
                    <td className="py-1 text-right font-mono">{formatMoney(it.desconto)}</td>
                    <td className="py-1 text-right font-mono">{formatMoney(it.quantidade * it.preco_unit - it.desconto)}</td>
                    <td className="py-1 text-xs">{it.eh_servico ? '🛠️ Serviço' : (it.origem_mp === 'cliente_traz' ? '🧳 Cliente' : '📦 Estoque')}</td>
                    <td className="py-1 text-right">
                      <button
                        className="text-red-500"
                        onClick={() => setItens(itens.filter((_, j) => j !== i))}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="font-bold">
                <td colSpan={4} className="text-right py-2">Total:</td>
                <td className="text-right font-mono">{formatMoney(total)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        )}

        {/* Split Peças × Serviços (mão de obra) — espelha os totais do backend */}
        {itens.length > 0 && (totalPecas > 0 || totalServicos > 0) && (
          <div className="flex flex-wrap gap-2 justify-end mb-3 text-xs">
            <span className="inline-flex items-center gap-1 bg-brand-50 text-brand-700 px-2 py-1 rounded-md">
              <Package size={12} /> Peças: <strong>{formatMoney(totalPecas)}</strong>
            </span>
            <span className="inline-flex items-center gap-1 bg-violet-50 text-violet-700 px-2 py-1 rounded-md">
              <Wrench size={12} /> Serviços: <strong>{formatMoney(totalServicos)}</strong>
            </span>
          </div>
        )}

        {/* Form adicionar item */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
          <div className="md:col-span-2 relative" ref={buscaRef}>
            {/* Abas Peças | Serviços — busca separada por tipo, colorida (peça=brand, serviço=violeta) */}
            <div className="flex gap-1 mb-1 p-0.5 bg-ink-100 rounded-lg w-fit">
              <button type="button" onClick={() => trocarAba('peca')}
                className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold transition-colors
                  ${abaBusca === 'peca' ? 'bg-white text-brand-700 shadow-sm' : 'text-ink-500 hover:text-ink-700'}`}>
                <Package size={13} /> Peças
              </button>
              <button type="button" onClick={() => trocarAba('servico')}
                className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold transition-colors
                  ${abaBusca === 'servico' ? 'bg-white text-violet-700 shadow-sm' : 'text-ink-500 hover:text-ink-700'}`}>
                <Wrench size={13} /> Serviços
              </button>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className="input pl-9"
                placeholder={abaBusca === 'servico' ? 'Buscar serviço (mão de obra)...' : 'Buscar peça (código ou nome)...'}
                value={buscaProd}
                autoComplete="off"
                onChange={e => {
                  setBuscaProd(e.target.value)
                  setAbrirBusca(true)
                  setDestaqueProd(0)       // texto mudou → volta destaque p/ o 1º
                  if (novoItem.produto_id) setNovoItem({ ...novoItem, produto_id: null })
                }}
                onFocus={() => setAbrirBusca(true)}
                // ↓↑ navega, Enter seleciona o destacado, Esc fecha
                onKeyDown={navegarLista(produtos, destaqueProd, setDestaqueProd,
                  selecionarProduto, () => setAbrirBusca(false))}
              />
            </div>
            {abrirBusca && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-20 max-h-80 overflow-y-auto">
                {produtos.length === 0 ? (
                  <div className="p-3 text-center text-sm text-gray-500">
                    Nenhum{abaBusca === 'servico' ? ' serviço' : 'a peça'} {buscaProd.trim() ? 'encontrado' : 'cadastrado'}
                  </div>
                ) : (
                  produtos.map((p: any, idx: number) => {
                    const ehServ = p.tipo === 'servico'
                    const ativo = idx === destaqueProd
                    return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => selecionarProduto(p)}
                      onMouseEnter={() => setDestaqueProd(idx)}
                      ref={el => { if (ativo) el?.scrollIntoView({ block: 'nearest' }) }}
                      className={`w-full px-3 py-2 text-left border-b last:border-0 flex items-center justify-between gap-2
                        ${ativo ? (ehServ ? 'bg-violet-50' : 'bg-brand-50') : 'hover:bg-gray-50'}`}
                    >
                      <div className="min-w-0 flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0
                          ${ehServ ? 'bg-violet-100 text-violet-700' : 'bg-brand-100 text-brand-700'}`}>
                          {ehServ ? 'SERV' : 'PEÇA'}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{p.descricao}</p>
                          <span className="text-xs text-gray-500 font-mono">{p.codigo}</span>
                        </div>
                      </div>
                      <span className="text-sm font-bold text-emerald-700 flex-shrink-0">{formatMoney(p.preco_venda)}</span>
                    </button>
                    )
                  })
                )}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-500">Qtd</label>
            <input
              type="number" step="0.001" min="0"
              className="input text-right"
              value={novoItem.quantidade}
              onChange={e => setNovoItem({ ...novoItem, quantidade: Number(e.target.value) })}
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">Preço</label>
            <input
              type="number" step="0.01" min="0"
              className="input text-right"
              value={novoItem.preco_unit}
              onChange={e => setNovoItem({ ...novoItem, preco_unit: Number(e.target.value) })}
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">Origem</label>
            {novoItem.eh_servico ? (
              // Serviço = mão de obra, não tem origem de estoque
              <div className="input bg-ink-50 text-ink-500 text-xs flex items-center gap-1 cursor-default">
                <Wrench size={12} className="text-violet-500" /> Mão de obra
              </div>
            ) : (
              <select
                className="input"
                value={novoItem.origem_mp}
                onChange={e => setNovoItem({ ...novoItem, origem_mp: e.target.value as any })}
              >
                <option value="estoque_proprio">Estoque</option>
                <option value="cliente_traz">Cliente traz</option>
              </select>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-500">Garantia (dias)</label>
            <input
              type="number" min="0"
              className="input text-right"
              value={novoItem.garantia_dias}
              onChange={e => setNovoItem({ ...novoItem, garantia_dias: Number(e.target.value) })}
            />
            {/* FEATURE 06/07: atalhos de prazo — o prazo é por item (cada serviço/peça o seu). */}
            <div className="flex flex-wrap gap-1 mt-1">
              {[['Sem', 0], ['90d', 90], ['180d', 180], ['1 ano', 365]].map(([lbl, d]) => (
                <button key={lbl} type="button"
                  onClick={() => setNovoItem({ ...novoItem, garantia_dias: d as number })}
                  className={`px-2 py-0.5 rounded text-[11px] font-medium border transition-colors ${
                    novoItem.garantia_dias === d
                      ? 'bg-brand-600 border-brand-600 text-white'
                      : 'bg-white border-ink-200 text-ink-600 hover:bg-ink-50'}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-2">
          <button
            type="button"
            className="btn-primary flex items-center gap-1"
            onClick={addItem}
          >
            <Plus className="w-4 h-4" /> Adicionar item
          </button>
          {novoItem.produto_id && (
            <span className="text-xs text-amber-600 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> Produto selecionado — clique em "Adicionar item" pra incluir na lista
            </span>
          )}
        </div>
      </div>

      {/* Submit */}
      <div className="flex justify-between items-center">
        <div className="text-lg">
          Total: <strong>{formatMoney(total)}</strong>
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={() => nav('/servicos')}>Cancelar</button>
          {!tecnicoId && (
            <div className="w-full mb-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <strong>Escolha o mecânico responsável</strong> para abrir no balcão ou
              iniciar a execução depois.
            </div>
          )}
          <button
            className="px-4 py-2 rounded-lg flex items-center gap-2 bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
            disabled={!clienteId || !tecnicoId || (itens.length === 0 && !novoItem.produto_id) || criarBalcao.isPending}
            onClick={() => criarBalcao.mutate()}
            title={!tecnicoId
              ? 'Escolha o mecânico responsável antes de cobrar no balcão'
              : 'Cria a OS já concluída e vai direto pro PDV cobrar (venda de balcão, sem etapa de aprovação)'}
          >
            <ShoppingCart className="w-4 h-4" />
            {criarBalcao.isPending ? 'Abrindo...' : 'Abrir e cobrar no balcão'}
          </button>
          <button
            className="btn-primary"
            disabled={!clienteId || criar.isPending}
            onClick={() => criar.mutate()}
          >
            {criar.isPending ? 'Criando...' : 'Criar OS'}
          </button>
        </div>
      </div>

      {!clienteId && (
        <div className="mt-3 text-sm text-amber-600 flex items-center gap-1">
          <AlertCircle className="w-4 h-4" /> Selecione o cliente pra criar a OS
        </div>
      )}

      {showNovaBike && clienteId && (
        <ModalNovaBike
          clienteId={clienteId}
          onClose={() => setShowNovaBike(false)}
          onCriada={(bike: any) => {
            qc.invalidateQueries({ queryKey: ['os-equipamentos', clienteId] })
            selecionarBike(bike)
            setShowNovaBike(false)
          }}
        />
      )}
      {showProntuario && (
        <ModalProntuario equipamentoId={showProntuario} onClose={() => setShowProntuario(null)} />
      )}
      {/* Cadastro rápido de cliente direto na OS (componente compartilhado c/ PDV) */}
      {showNovoCliente && (
        <ModalNovoCliente
          nomeInicial={buscaCliente.trim()}
          onClose={() => setShowNovoCliente(false)}
          onCriado={(p: any) => {
            selecionarCliente(p)
            setShowNovoCliente(false)
          }}
        />
      )}
    </div>
  )
}

// Preset de campos da bike (genérico com preset Bike)
const CAMPOS_BIKE = ['aro', 'tamanho do quadro', 'suspensão', 'freio', 'marchas']

function ModalNovaBike({ clienteId, onClose, onCriada }: {
  clienteId: number; onClose: () => void; onCriada: (b: any) => void
}) {
  const [marca, setMarca] = useState('')
  const [modelo, setModelo] = useState('')
  const [ano, setAno] = useState('')
  const [cor, setCor] = useState('')
  const [numeroSerie, setNumeroSerie] = useState('')
  const [extra, setExtra] = useState<Record<string, string>>({})
  const salvar = useMutation({
    mutationFn: () => api.post('/servicos/equipamentos', {
      cliente_id: clienteId, tipo: 'bike',
      marca: marca || undefined, modelo: modelo || undefined, ano: ano || undefined,
      cor: cor || undefined, numero_serie: numeroSerie || undefined,
      campos_custom: Object.fromEntries(Object.entries(extra).filter(([, v]) => v)),
    }),
    onSuccess: (r: any) => { toast.success('Bike cadastrada'); onCriada(r.data) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao cadastrar'),
  })
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Bike size={18} className="text-brand-600" /> Nova bike</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-semibold text-gray-500 uppercase">Marca</label>
            <input value={marca} onChange={e => setMarca(e.target.value.toUpperCase())} autoFocus className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" /></div>
          <div><label className="text-xs font-semibold text-gray-500 uppercase">Modelo</label>
            <input value={modelo} onChange={e => setModelo(e.target.value.toUpperCase())} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" /></div>
          <div><label className="text-xs font-semibold text-gray-500 uppercase">Ano</label>
            <input value={ano} onChange={e => setAno(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" /></div>
          <div><label className="text-xs font-semibold text-gray-500 uppercase">Cor</label>
            <input value={cor} onChange={e => setCor(e.target.value.toUpperCase())} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" /></div>
          <div className="col-span-2"><label className="text-xs font-semibold text-gray-500 uppercase">Nº de série / quadro</label>
            <input value={numeroSerie} onChange={e => setNumeroSerie(e.target.value.toUpperCase())} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" /></div>
          {CAMPOS_BIKE.map(c => (
            <div key={c}><label className="text-xs font-semibold text-gray-500 uppercase">{c}</label>
              <input value={extra[c] || ''} onChange={e => setExtra({ ...extra, [c]: e.target.value })}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" /></div>
          ))}
        </div>
        <button onClick={() => salvar.mutate()} disabled={salvar.isPending}
          className="mt-4 w-full btn-primary disabled:opacity-50">
          {salvar.isPending ? 'Salvando...' : 'Cadastrar bike'}
        </button>
      </div>
    </div>
  )
}

function ModalProntuario({ equipamentoId, onClose }: { equipamentoId: number; onClose: () => void }) {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['prontuario', equipamentoId],
    queryFn: async () => (await api.get(`/servicos/equipamentos/${equipamentoId}/prontuario`)).data,
  })
  const e = data?.equipamento
  const hist = data?.historico || []
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={ev => ev.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><FileText size={18} className="text-brand-600" /> Prontuário</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
          </div>
          {e && (
            <div className="mt-1">
              <div className="font-semibold text-gray-900">{e.descricao}{e.cor ? ` · ${e.cor}` : ''}</div>
              <div className="text-xs text-gray-500">
                {e.numero_serie ? `nº ${e.numero_serie}` : ''}
                {Object.entries(e.campos_custom || {}).map(([k, v]) => ` · ${k}: ${v}`).join('')}
              </div>
            </div>
          )}
        </div>
        <div className="p-4 overflow-y-auto">
          {isLoading ? (
            <div className="text-center text-gray-400 py-8">Carregando...</div>
          ) : hist.length === 0 ? (
            <div className="text-center text-gray-400 py-8">Nenhum serviço no histórico ainda.</div>
          ) : (
            <div className="space-y-2">
              {hist.map((h: any) => (
                <div key={h.os_id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm">OS #{h.numero}</span>
                    <span className="text-sm font-bold text-emerald-700">{formatMoney(h.valor_total)}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {h.data_abertura ? new Date(h.data_abertura).toLocaleDateString('pt-BR') : ''} · {h.status}
                  </div>
                  {h.servicos.length > 0 && (
                    <div className="text-xs text-gray-700 mt-1">{h.servicos.join(', ')}</div>
                  )}
                  {h.descricao_problema && <div className="text-[11px] text-gray-500 mt-0.5 italic">"{h.descricao_problema}"</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
